import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { getFinanceElfLoansList } from '../../endpoints/financeElfLoans';
import type { ElfLoanListItem } from '../../types/financeElfLoans';
import { getApiErrorMessage } from '../../utils/httpClientTs';

export interface ElfBorrowerChoice {
  id: string;
  name: string;
}

interface ElfBorrowerPickerProps {
  selected: ElfBorrowerChoice | null;
  onSelect: (borrower: ElfBorrowerChoice) => void;
}

const ElfBorrowerPicker: React.FC<ElfBorrowerPickerProps> = ({
  selected,
  onSelect,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ElfLoanListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setBusy(false);
      return;
    }
    let cancel = false;
    const timer = window.setTimeout(() => {
      setBusy(true);
      setError(null);
      getFinanceElfLoansList({ page: 1, page_size: 8, search: term })
        .then((res) => {
          if (!cancel) setResults(res.data ?? []);
        })
        .catch((err) => {
          if (!cancel) setError(getApiErrorMessage(err));
        })
        .finally(() => {
          if (!cancel) setBusy(false);
        });
    }, 300);
    return () => {
      cancel = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Borrower: {selected?.name || selected?.id || 'None selected'}
      </Typography>
      <TextField
        label="Search borrowers"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        fullWidth
        size="small"
        placeholder="Name, email, or iMIS id"
      />
      {busy && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Searching…
          </Typography>
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      {results.length > 0 && (
        <List dense sx={{ mt: 1, maxHeight: 220, overflow: 'auto' }}>
          {results.map((row) => (
            <ListItemButton
              key={row.id}
              selected={selected?.id === row.id}
              onClick={() =>
                onSelect({
                  id: row.id,
                  name: row.borrower_name || row.imis_id || row.id,
                })
              }
            >
              <ListItemText
                primary={row.borrower_name || row.id}
                secondary={[row.imis_id, row.borrower_email]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ElfBorrowerPicker;
